import React, { useEffect, useState, useContext, useMemo } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import brLocale from 'date-fns/locale/pt-BR';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { Button, Grid, TextField } from '@material-ui/core';
import { Box } from '@mui/material';
import api from '../../services/api';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { i18n } from '../../translate/i18n';
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
    chartWrap: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        borderRadius: 12,
        minHeight: 280,
        backgroundColor: theme.palette.type === 'dark'
            ? 'rgba(30, 41, 59, 0.5)'
            : 'rgba(248, 250, 252, 0.8)',
        border: theme.palette.type === 'dark'
            ? '1px solid rgba(148,163,184,0.12)'
            : '1px solid rgba(15,23,42,0.06)',
    },
}));

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

export const ChatsUser = () => {
    const classes = useStyles();
    const theme = useTheme();
    const isDark = theme.palette.type === 'dark';
    const [initialDate, setInitialDate] = useState(new Date());
    const [finalDate, setFinalDate] = useState(new Date());
    const [ticketsData, setTicketsData] = useState({ data: [] });
    const { user } = useContext(AuthContext);

    const companyId = user.companyId;

    useEffect(() => {
        if (companyId) {
            handleGetTicketsInformation();
        }
    }, [companyId]);

    const chartOptions = useMemo(() => ({
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: { display: false },
            datalabels: {
                display: true,
                anchor: 'end',
                align: 'end',
                offset: 4,
                color: isDark ? '#f1f5f9' : '#0f172a',
                font: { size: 12, weight: '600' },
            },
            tooltip: {
                backgroundColor: isDark ? '#1e293b' : '#ffffff',
                titleColor: isDark ? '#f1f5f9' : '#0f172a',
                bodyColor: isDark ? '#94a3b8' : '#64748b',
                borderColor: isDark ? 'rgba(148,163,184,0.2)' : 'rgba(15,23,42,0.08)',
                borderWidth: 1,
                cornerRadius: 10,
                padding: 12,
            },
        },
        scales: {
            x: {
                beginAtZero: true,
                grid: {
                    color: isDark ? 'rgba(148, 163, 184, 0.12)' : 'rgba(15, 23, 42, 0.06)',
                    drawBorder: false,
                },
                ticks: {
                    color: isDark ? '#94a3b8' : '#64748b',
                    font: { size: 11 },
                },
            },
            y: {
                grid: { display: false },
                ticks: {
                    color: isDark ? '#cbd5e1' : '#334155',
                    font: { size: 12, weight: '500' },
                },
            },
        },
    }), [isDark]);

    const dataCharts = {
        labels: ticketsData?.data?.length > 0
            ? ticketsData.data.map((item) => item.nome)
            : [],
        datasets: [
            {
                data: ticketsData?.data?.length > 0
                    ? ticketsData.data.map((item) => item.quantidade)
                    : [],
                backgroundColor: theme.palette.primary.main,
                hoverBackgroundColor: theme.palette.primary.dark || theme.palette.primary.main,
                borderRadius: 8,
                barThickness: 22,
                maxBarThickness: 28,
            },
        ],
    };

    const handleGetTicketsInformation = async () => {
        try {
            const { data } = await api.get(
                `/dashboard/ticketsUsers?initialDate=${format(initialDate, 'yyyy-MM-dd')}&finalDate=${format(finalDate, 'yyyy-MM-dd')}&companyId=${companyId}`
            );
            setTicketsData(data);
        } catch (error) {
            toast.error('Error al buscar información de los tickets');
        }
    };

    return (
        <Box>
            <Grid container spacing={2} alignItems="center">
                <Grid item>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
                        <DatePicker
                            value={initialDate}
                            onChange={(newValue) => { setInitialDate(newValue); }}
                            label={i18n.t("dashboard.date.initialDate")}
                            renderInput={(params) => <TextField fullWidth {...params} sx={{ width: '20ch' }} />}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
                        <DatePicker
                            value={finalDate}
                            onChange={(newValue) => { setFinalDate(newValue); }}
                            label={i18n.t("dashboard.date.finalDate")}
                            renderInput={(params) => <TextField fullWidth {...params} sx={{ width: '20ch' }} />}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item>
                    <Button
                        onClick={handleGetTicketsInformation}
                        variant="contained"
                        color="primary"
                        style={{ marginTop: 8 }}
                    >
                        {i18n.t("dashboard.buttons.filter") || "Filtrar"}
                    </Button>
                </Grid>
            </Grid>
            <div className={classes.chartWrap}>
                <Bar
                    options={chartOptions}
                    data={dataCharts}
                    style={{ maxWidth: '100%', height: Math.max(220, (ticketsData?.data?.length || 3) * 44) }}
                />
            </div>
        </Box>
    );
};
